import React, { useState, useMemo } from 'react';
import { getCustomers, deleteCustomer, deleteAllCustomers } from '../../services/api';
import { Plus, Edit2, Trash2, Search, Building2, User, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import Pagination from '../../components/Pagination';
import CustomerForm from './CustomerForm';

export default function CustomersTab() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [typeFilter, setTypeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    React.useEffect(() => { fetchCustomers(); }, []);

    const fetchCustomers = async () => {
        try { setCustomers((await getCustomers()).data); }
        catch { toast.error('Musteriler yuklenemedi'); }
        finally { setLoading(false); }
    };

    const openForm = (customer = null) => { setEditingCustomer(customer); setView('form'); };
    const closeForm = () => { setEditingCustomer(null); setView('list'); };
    const handleSaved = () => { closeForm(); fetchCustomers(); };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu musteriyi silmek istediginizden emin misiniz?')) return;
        try { await deleteCustomer(id); toast.success('Musteri silindi'); fetchCustomers(); selected.delete(id); setSelected(new Set(selected)); }
        catch { toast.error('Musteri silinemedi'); }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`${selected.size} musteriyi silmek istediginizden emin misiniz?`)) return;
        try {
            await Promise.all([...selected].map(id => deleteCustomer(id)));
            toast.success(`${selected.size} musteri silindi`);
            setSelected(new Set());
            fetchCustomers();
        } catch { toast.error('Toplu silme basarisiz'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm(`Tum musterileri (${customers.length}) silmek istediginizden emin misiniz? Bu islem geri alinamaz!`)) return;
        try {
            const res = await deleteAllCustomers();
            toast.success(res.data.message || 'Tum musteriler silindi');
            setSelected(new Set());
            fetchCustomers();
        } catch { toast.error('Toplu silme basarisiz'); }
    };

    const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => {
        const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        const allSelected = pageItems.every(c => selected.has(c.id));
        const n = new Set(selected);
        pageItems.forEach(c => allSelected ? n.delete(c.id) : n.add(c.id));
        setSelected(n);
    };

    const handleExport = () => {
        if (!customers.length) return;
        const rows = customers.map(c => ({
            'Ad': c.name, 'Firma': c.company_name || '', 'Tip': c.customer_type === 'corporate' ? 'Kurumsal' : 'Bireysel',
            'Email': c.email || '', 'Telefon': c.phone || '', 'KDV No': c.tax_number || '',
            'Vergi Dairesi': c.tax_office || '', 'Yetkili': c.contact_person || '',
            'Adres': c.address || '', 'Sehir': c.city || '', 'Ulke': c.country || '',
            'Posta Kodu': c.postal_code || '', 'Fiyat Grubu': c.price_group || '', 'Para Birimi': c.currency || '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Musteriler');
        XLSX.writeFile(wb, `musteriler_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Excel dosyasi indirildi');
    };

    const filtered = useMemo(() => {
        setPage(1);
        let list = customers;
        if (typeFilter !== 'all') list = list.filter(c => c.customer_type === typeFilter);
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            list = list.filter(c =>
                c.name.toLowerCase().includes(q) ||
                (c.company_name && c.company_name.toLowerCase().includes(q)) ||
                (c.email && c.email.toLowerCase().includes(q)) ||
                (c.phone && c.phone.includes(searchTerm)) ||
                (c.city && c.city.toLowerCase().includes(q))
            );
        }
        return list;
    }, [customers, searchTerm, typeFilter]);

    if (view === 'form') {
        return <CustomerForm customer={editingCustomer} onSaved={handleSaved} onCancel={closeForm} />;
    }

    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const allPageSelected = pageItems.length > 0 && pageItems.every(c => selected.has(c.id));

    return (
        <div className="space-y-4" data-testid="customers-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-sm text-[#6B7280]">{customers.length} musteri kayitli</p>
                <div className="flex items-center gap-2 flex-wrap">
                    {selected.size > 0 && (
                        <>
                            <button onClick={handleBulkDelete} className="btn btn-danger text-sm" data-testid="bulk-delete-btn">
                                <Trash2 size={14} /> Sil ({selected.size})
                            </button>
                            <button onClick={handleDeleteAll} className="btn btn-danger text-sm" data-testid="delete-all-customers-btn">
                                <Trash2 size={14} /> Tumunu Sil
                            </button>
                        </>
                    )}
                    <button onClick={handleExport} className="btn btn-secondary text-sm" data-testid="export-customers-btn">
                        <FileSpreadsheet size={14} /> Excel
                    </button>
                    <button onClick={() => openForm()} className="btn btn-primary text-sm" data-testid="add-customer-btn">
                        <Plus size={16} /> Yeni Musteri
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="Musteri ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="form-input pl-10" data-testid="customer-search-input" />
                </div>
                <div className="flex gap-1">
                    {[{k:'all',l:'Tumu'},{k:'corporate',l:'Kurumsal'},{k:'individual',l:'Bireysel'}].map(b => (
                        <button key={b.k} onClick={() => setTypeFilter(b.k)} className={`btn text-sm ${typeFilter === b.k ? 'btn-primary' : 'btn-secondary'}`} data-testid={`customer-type-filter-${b.k}`}>{b.l}</button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
            ) : filtered.length > 0 ? (
                <div className="data-card">
                    <div className="table-container">
                        <table className="table mobile-cards" data-testid="customers-table">
                            <colgroup>
                                <col style={{ width: '40px' }} />
                                <col />
                                <col style={{ width: '90px' }} />
                                <col style={{ width: '140px' }} />
                                <col style={{ width: '100px' }} />
                                <col style={{ width: '90px' }} />
                                <col style={{ width: '60px' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th><input type="checkbox" checked={allPageSelected} onChange={toggleAll} className="rounded border-gray-300" data-testid="select-all-customers" /></th>
                                    <th>Musteri</th>
                                    <th className="mobile-hide">Tip</th>
                                    <th className="mobile-hide">Telefon</th>
                                    <th className="mobile-hide">Sehir</th>
                                    <th className="mobile-hide">Fiyat</th>
                                    <th className="text-right">Islem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map(c => (
                                    <tr key={c.id} className={selected.has(c.id) ? 'bg-blue-50/50' : ''} data-testid={`customer-row-${c.id}`}>
                                        <td onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded border-gray-300" data-testid={`select-customer-${c.id}`} />
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="w-8 h-8 rounded-xl bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                                                    {c.customer_type === 'corporate' ? <Building2 size={14} className="text-[#007AFF]" /> : <User size={14} className="text-[#007AFF]" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="font-medium text-[#1D1D1F] text-sm block truncate">{c.name}</span>
                                                    {c.email && <span className="text-[11px] text-[#86868B] block truncate">{c.email}</span>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="mobile-hide">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium ${c.customer_type === 'corporate' ? 'bg-[#5856D6]/10 text-[#5856D6]' : 'bg-[#FF9500]/10 text-[#FF9500]'}`}>
                                                {c.customer_type === 'corporate' ? 'Kurumsal' : 'Bireysel'}
                                            </span>
                                        </td>
                                        <td className="mobile-hide text-sm text-[#86868B] truncate">{c.phone || '\u2014'}</td>
                                        <td className="mobile-hide text-sm text-[#86868B] truncate">{c.city || '\u2014'}</td>
                                        <td className="mobile-hide">
                                            {c.price_group ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-[#F5F5F7] text-[#1D1D1F] text-[11px] font-semibold">Fiyat {c.price_group}</span> : <span className="text-sm text-[#86868B]">{'\u2014'}</span>}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <button onClick={() => openForm(c)} className="p-1.5 rounded-lg hover:bg-[#F5F5F7] transition-colors" title="Duzenle" data-testid={`edit-customer-${c.id}`}>
                                                    <Edit2 size={15} className="text-[#007AFF]" />
                                                </button>
                                                <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 transition-colors" title="Sil" data-testid={`delete-customer-${c.id}`}>
                                                    <Trash2 size={15} className="text-[#FF3B30]" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PAGE_SIZE)} onPageChange={setPage} />
                </div>
            ) : customers.length > 0 ? (
                <div className="text-center py-12">
                    <Search className="w-12 h-12 text-[#C7C7CC] mx-auto mb-3" />
                    <p className="text-[#86868B]">Aramanizla eslesen musteri bulunamadi</p>
                </div>
            ) : (
                <div className="text-center py-16">
                    <Building2 className="w-16 h-16 text-[#C7C7CC] mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">Henuz musteri yok</h3>
                    <p className="text-[#86868B] mb-6">Satis surecini baslatmak icin musteri ekleyin</p>
                    <button onClick={() => openForm()} className="btn btn-primary" data-testid="add-customer-empty-btn">
                        <Plus size={16} /> Yeni Musteri
                    </button>
                </div>
            )}
        </div>
    );
}
